"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.LowBandwidthMetricsObserver = exports.LOW_BANDWIDTH_THRESHOLD = exports.MAX_METRICS_TO_KEEP = void 0;
const streem_logger_1 = require("../util/streem_logger");
const get_video_metrics_for_remote_user_1 = require("../util/get_video_metrics_for_remote_user");
const array_helpers_1 = require("../util/array_helpers");
const bandwidth_level_1 = require("./bandwidth_level");
// From looking at the Chime SDK source code, it appears that the minimum interval for metrics is
//    1 second.  I haven't seen this documented anywhere, so it's possible that it could change. This
//    code will ensure that metric logging does not happen more frequently than this interval.
//
// Note that the metrics do not come at exactly 1 second intervals, so this number is slightly less
//    to allow for some variations.
const CAPTURE_METRICS_MIN_INTERVAL_MS = 950;
exports.MAX_METRICS_TO_KEEP = 10;
exports.LOW_BANDWIDTH_THRESHOLD = 1500 * 1000; // 1.5 Mbps
class LowBandwidthMetricsObserver {
    constructor(attendeeId, // AWS attendee Id
    localVideoTrackEnabledObservable, remoteVideoTrackEnabledObservable, onBandwidthLevelDetermined, logger = streem_logger_1.streemLogger.extend('sdk-chime-video:LowBandwidthMetricsObserver')) {
        this.attendeeId = attendeeId;
        this.localVideoTrackEnabledObservable = localVideoTrackEnabledObservable;
        this.remoteVideoTrackEnabledObservable = remoteVideoTrackEnabledObservable;
        this.onBandwidthLevelDetermined = onBandwidthLevelDetermined;
        this.logger = logger;
        this.isLocalVideoTrackEnabled = false;
        this.isRemoteVideoTrackEnabled = false;
        this.metrics = [];
        this.metricsDidReceive = (clientMetricReport) => {
            var _a;
            const msSinceLastMetricsReceived = Date.now() - this.lastMetricsCaptureTime;
            if (msSinceLastMetricsReceived < CAPTURE_METRICS_MIN_INTERVAL_MS) {
                this.logger.debug(`Low bandwidth observer: Chime metrics received too soon after last capture: ${msSinceLastMetricsReceived} ms.  Ignoring.`);
                return;
            }
            const { videoUpstreamBitrate, availableOutgoingBitrate, availableIncomingBitrate } = clientMetricReport.getObservableMetrics();
            // Note: videoDownstreamBitrate is not available in the observable metrics.  It is per
            //       participant (although Streem calls only have 1 remote participant)
            const videoMetricsForRemoteUser = (0, get_video_metrics_for_remote_user_1.getVideoMetricsForRemoteUser)(clientMetricReport.getObservableVideoMetrics(), this.attendeeId, this.logger);
            const videoDownstreamBitrate = (_a = videoMetricsForRemoteUser === null || videoMetricsForRemoteUser === void 0 ? void 0 : videoMetricsForRemoteUser.videoDownstreamBitrate) !== null && _a !== void 0 ? _a : 0;
            const relevantMetrics = {
                availableUpstreamBitrate: availableOutgoingBitrate,
                videoUpstreamBitrate,
                availableDownstreamBitrate: availableIncomingBitrate,
                videoDownstreamBitrate,
                localVideoTrackEnabled: this.isLocalVideoTrackEnabled,
                remoteVideoTrackEnabled: this.isRemoteVideoTrackEnabled,
            };
            this.logger.debug(`Low bandwidth observer metrics received: ${JSON.stringify(relevantMetrics)}`);
            this.updateMetrics(relevantMetrics);
            const bandwidthLevel = this.determineBandwidthLevel(this.isLocalVideoTrackEnabled, this.isRemoteVideoTrackEnabled, availableOutgoingBitrate, availableIncomingBitrate);
            if (bandwidthLevel !== bandwidth_level_1.BandwidthLevel.NONE) {
                this.onBandwidthLevelDetermined(bandwidthLevel);
            }
            this.lastMetricsCaptureTime = Date.now();
        };
        this.lastMetricsCaptureTime = Date.now() - CAPTURE_METRICS_MIN_INTERVAL_MS;
        this.localVideoTrackEnableSubscription = this.localVideoTrackEnabledObservable.subscribe(isEnabled => {
            this.isLocalVideoTrackEnabled = isEnabled;
        });
        this.remoteVideoTrackEnableSubscription = this.remoteVideoTrackEnabledObservable.subscribe(isEnabled => {
            this.isRemoteVideoTrackEnabled = isEnabled;
        });
    }
    disconnect() {
        this.localVideoTrackEnableSubscription.unsubscribe();
        this.remoteVideoTrackEnableSubscription.unsubscribe();
    }
    determineBandwidthLevel(localVideoTrackEnabled, remoteVideoTrackEnabled, availableOutgoingBitrate, availableIncomingBitrate) {
        if (!localVideoTrackEnabled && !remoteVideoTrackEnabled) {
            this.logger.info('No video tracks enabled, not emitting a bandwidth level');
            return bandwidth_level_1.BandwidthLevel.NONE;
        }
        const availableBitrate = availableOutgoingBitrate + availableIncomingBitrate;
        if (availableBitrate < exports.LOW_BANDWIDTH_THRESHOLD) {
            return bandwidth_level_1.BandwidthLevel.LOW;
        }
        return bandwidth_level_1.BandwidthLevel.NORMAL;
    }
    updateMetrics(newMetrics) {
        (0, array_helpers_1.addToArrayKeepingMostRecentItems)(this.metrics, newMetrics, exports.MAX_METRICS_TO_KEEP);
    }
}
exports.LowBandwidthMetricsObserver = LowBandwidthMetricsObserver;
//# sourceMappingURL=low_bandwidth_metrics_observer.js.map